import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Volume2 } from 'lucide-react';
import { wistiaEmbed } from '../ThreeJobs';
import { EditCtx, PageCtx, ctaGo, markupToHtml } from './EditableBits';

const API = process.env.REACT_APP_BACKEND_URL;
const isFileVideo = (u) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u || '');

/* ── Floating reel video — desktop only, bottom-right, follows scroll ── */
export const FloatingReel = ({ reel, secId = '_page' }) => {
  const { handlers, config } = useContext(PageCtx);
  const edit = useContext(EditCtx);
  const [muted, setMuted] = useState(true);
  const [closed, setClosed] = useState(false);
  const vidRef = useRef(null);
  const r = reel || {};
  if (!r.enabled || closed) return null;
  const url = r.video_url || '';
  const embed = wistiaEmbed(url);
  const file = !embed && isFileVideo(url);
  if (!embed && !file && !edit) return null;
  const c = r.cta || {};
  const selectMe = () => { if (edit) edit.onSelect(secId, 'reel'); };
  const unmute = (e) => {
    e.stopPropagation();
    if (edit) { selectMe(); return; }
    setMuted(false);
    if (file && vidRef.current) {
      const v = vidRef.current;
      v.muted = false;
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };
  const onCta = (e) => {
    e.stopPropagation();
    if (edit) { selectMe(); return; }
    if (ctaGo(c) === 'default' && handlers.openQuiz) handlers.openQuiz();
  };
  return (
    <div
      className={`hidden md:flex fixed bottom-5 right-5 z-40 w-[220px] flex-col rounded-2xl overflow-hidden bg-[#141414] border border-black/20 shadow-[0_24px_70px_rgba(0,0,0,0.4)] ${edit ? 'vx-editable' : ''}`}
      data-testid="floating-reel"
      onClick={edit ? selectMe : undefined}
    >
      <div className="relative aspect-[9/16] bg-black">
        {embed && (
          <iframe
            key={muted ? 'reel-m' : 'reel-u'}
            src={`${embed}&autoPlay=true&muted=${muted ? 'true' : 'false'}&silentAutoPlay=allow&playbar=false&volumeControl=false&smallPlayButton=false&settingsControl=false&fullscreenButton=false&endVideoBehavior=loop&fitStrategy=cover&controlsVisibleOnLoad=false`}
            title="reel"
            allow="autoplay; fullscreen"
            className={`w-full h-full ${muted || edit ? 'pointer-events-none' : ''}`}
          />
        )}
        {file && (
          <video ref={vidRef} src={url} muted autoPlay loop playsInline className="w-full h-full object-cover" />
        )}
        {!embed && !file && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center">
            <span className="arc-body text-[11px] font-bold text-white/50">Add a video in Page design → Floating reel</span>
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); if (edit) { selectMe(); return; } setClosed(true); }}
          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/60 text-white/90 flex items-center justify-center hover:bg-black/80 transition-colors"
          data-testid="reel-close-btn"
          aria-label="Close video"
        >
          <X size={13} />
        </button>
        {muted && (embed || file) && (
          <button onClick={unmute} className="absolute inset-0 flex flex-col items-center justify-end pb-4 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-testid="reel-unmute-overlay">
            <span className="absolute top-2 left-2 arc-body inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-bold px-2.5 py-1">
              <Volume2 size={10} /> Unmute
            </span>
            <span className="arc-body rounded-full bg-white text-[#141414] text-xs font-black px-4 py-2 shadow-lg">
              {r.overlay || 'Click here to unmute'}
            </span>
          </button>
        )}
      </div>
      {(c.label || edit) && (
        <button
          onClick={onCta}
          className="arc-body w-full py-3 px-3 text-sm font-black text-center transition-opacity hover:opacity-90"
          style={{ background: c.bg || '#141414', color: c.fg || '#F0EFEC' }}
          data-testid="reel-cta-btn"
        >
          <span dangerouslySetInnerHTML={{ __html: markupToHtml(c.label || 'Save my free seat', { config, strongClass: 'font-black' }) }} />
        </button>
      )}
    </div>
  );
};

/* ── Social-proof registration popups — bottom-left ── */
const GHOSTS = [
  ['Alicia D.', 'Toronto, CA'], ['Marcus R.', 'Austin, TX'], ['Priya S.', 'London, UK'],
  ['Jake T.', 'Phoenix, AZ'], ['Danielle O.', 'Miami, FL'], ['Tom H.', 'Denver, CO'],
  ['Sofia M.', 'San Diego, CA'], ['Liam K.', 'Chicago, IL'], ['Emma W.', 'Nashville, TN'],
  ['Noah B.', 'Tampa, FL'], ['Olivia P.', 'Dallas, TX'], ['Ethan C.', 'Seattle, WA'],
  ['Maya G.', 'Atlanta, GA'], ['Ryan F.', 'Charlotte, NC'], ['Chloe A.', 'Portland, OR'], ['Dylan S.', 'Las Vegas, NV'],
];
const AVATAR_BG = ['#16A34A', '#2563EB', '#D97706', '#7C3AED', '#DC2626', '#0891B2'];
const avatarBg = (name) => AVATAR_BG[[...String(name)].reduce((a, ch) => a + ch.charCodeAt(0), 0) % AVATAR_BG.length];
const initials = (name) => String(name).split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const relTime = (iso) => {
  const t = Date.parse(iso);
  if (!t) return 'recently';
  const m = Math.max(1, Math.round((Date.now() - t) / 60000));
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return d <= 7 ? `${d} day${d === 1 ? '' : 's'} ago` : 'this week';
};

export const SocialProof = ({ proof }) => {
  const edit = useContext(EditCtx);
  const [toast, setToast] = useState(null);
  const dataRef = useRef({ real: [], ri: 0, gi: Math.floor(Math.random() * GHOSTS.length) });
  const s = proof || {};

  useEffect(() => {
    if (!s.enabled || edit) return undefined;
    let alive = true;
    axios.get(`${API}/api/funnel/social-proof`)
      .then((res) => { if (alive) dataRef.current.real = (res.data && res.data.registrations) || []; })
      .catch(() => {});
    const pop = (t) => {
      if (!alive) return;
      setToast({ ...t, key: Date.now() });
      setTimeout(() => { if (alive) setToast(null); }, 6000);
    };
    const showReal = () => {
      const d = dataRef.current;
      if (!d.real.length) return;
      const e = d.real[d.ri % d.real.length];
      d.ri += 1;
      pop({ name: e.name, place: e.country, at: e.at, ghost: false });
    };
    const showGhost = () => {
      const d = dataRef.current;
      const g = GHOSTS[d.gi % GHOSTS.length];
      d.gi += 1;
      pop({ name: g[0], place: g[1], mins: 1 + Math.floor(Math.random() * 18), ghost: true });
    };
    const first = setTimeout(() => { if (dataRef.current.real.length) showReal(); else if (s.ghosts) showGhost(); }, 4000);
    const t1 = setInterval(showReal, Math.max(5, +s.every_s || 12) * 1000);
    const t2 = s.ghosts ? setInterval(showGhost, Math.max(5, +s.ghost_every_s || 25) * 1000) : null;
    return () => { alive = false; clearTimeout(first); clearInterval(t1); if (t2) clearInterval(t2); };
  }, [s.enabled, s.every_s, s.ghosts, s.ghost_every_s, edit]);

  const show = edit && s.enabled ? { name: 'Alicia D.', place: 'Toronto, CA', mins: 1, ghost: true, key: 'demo' } : toast;
  if (!s.enabled || !show) return null;
  return (
    <div key={show.key} className="fixed bottom-4 left-4 z-40 vx-proof-in" data-testid="social-proof-toast">
      <div className="flex items-center gap-3 rounded-2xl bg-white border border-black/[0.08] shadow-[0_16px_50px_rgba(20,20,20,0.18)] pl-3 pr-5 py-3 max-w-[330px]">
        <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-black shrink-0" style={{ background: avatarBg(show.name) }}>
          {initials(show.name)}
        </span>
        <div className="min-w-0">
          <p className="arc-body text-[13px] font-black text-[#141414] truncate">
            {show.name}{show.place ? <span className="font-semibold text-[#141414]/45"> · {show.place}</span> : null}
          </p>
          <p className="arc-body text-xs text-[#141414]/65">just registered — welcome aboard! 🎉</p>
          <p className="arc-body text-[10px] text-[#141414]/40 mt-0.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
            {show.ghost ? `${show.mins} minute${show.mins === 1 ? '' : 's'} ago` : relTime(show.at)}
          </p>
        </div>
      </div>
    </div>
  );
};

/* Reads the pinned page_settings section and renders both widgets */
export const PageWidgets = ({ sections }) => {
  const ps = (sections || []).find((x) => x.type === 'page_settings');
  const props = (ps && ps.props) || {};
  return (
    <>
      <FloatingReel reel={props.reel} secId={(ps && ps.id) || '_page'} />
      <SocialProof proof={props.proof} />
    </>
  );
};
